import { SxProps, Typography } from "@mui/material";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Box, FlexColBox, Icon } from "../../../common/components";
import { createStyles, palette } from "../../../styles/theme";
import { getConditionnalStyle, mergeSx } from "../../../utils/themeUtils";
import { MaterialSymbolType } from "../../../common/components/Icon/IconConstant";
import { ProductLineType } from "../../../types";

const styles = createStyles({
  container: {
    cursor: "pointer",
    display: "flex",
    maxWidth: "fit-content",
    padding: "6px 12px 6px 10px",
    alignItems: "center",
    borderRadius: "12px",
    border: {
      xs: "none",
      lg: `1px solid ${palette.grey.darkGrey}`,
    },
    gap: "8px",
    userSelect: "none",
    margin: {
      xs: "0",
      lg: "1px",
    },
    ":hover": {
      margin: {
        xs: "0",
        lg: "1px",
      },
      background: {
        xs: "none",
        lg: `linear-gradient(90deg, rgba(84, 172, 222, 0.04) 0%, rgba(169, 84, 222, 0.04) 100%), ${palette.lightBorderGradient}`,
      },
      border: {
        xs: "none",
        lg: `1px solid transparent`,
      },
    },
  },
  active: {
    background: {
      xs: "linear-gradient(90deg, rgba(84, 172, 222, 0.04) 0%, rgba(169, 84, 222, 0.04) 100%)",
      lg: `linear-gradient(white, white) padding-box, ${palette.borderGradient}`,
    },
    margin: "0",
    border: {
      xs: "none",
      lg: `2px solid transparent`,
    },
    ":hover": {
      margin: "0",
      background: {
        xs: "none",
        lg: `linear-gradient(white, white) padding-box, ${palette.borderGradient}`,
      },
      border: {
        xs: "none",
        lg: `2px solid transparent`,
      },
    },
  },
  disabled: {
    opacity: 0.5,
    margin: {
      xs: "0",
      lg: "1px",
    },
    border: {
      xs: "none",
      lg: `1px solid ${palette.grey.lightGrey} !important`,
    },
    cursor: "default",
    pointerEvents: "none",
    "& .label, & .description": {
      color: `${palette.grey.darkerGrey} !important`,
    },
    "& .icon": {
      color: `${palette.grey.darkGrey} !important`,
    },
  },
});

interface Props {
  lineType: ProductLineType;
  color: string;
  labelKey: string;
  icon: MaterialSymbolType;
  onClick: (lineType: ProductLineType) => void;
  active?: boolean;
  description?: string;
  disabled?: boolean;
  containerSx?: SxProps;
  iconSize?: number;
}

const ProductLineFilterChip = ({
  lineType,
  color,
  labelKey,
  icon,
  active,
  onClick,
  description,
  disabled,
  containerSx,
  iconSize,
}: Props) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={mergeSx(
        styles.container,
        getConditionnalStyle(active, styles.active),
        getConditionnalStyle(disabled, styles.disabled),
        getConditionnalStyle(!!containerSx, containerSx)
      )}
      onClick={!disabled ? () => onClick(lineType) : undefined}
    >
      {icon && (
        <Icon
          icon={icon}
          size={iconSize ?? 12}
          color={color}
          className="icon"
        />
      )}
      <FlexColBox gap={"2px"}>
        <Typography variant="textSm" color={color} className="label">
          {t(labelKey)}
        </Typography>
        <Typography
          variant="textXs"
          color={palette.grey.primary}
          className="description"
        >
          {description}
        </Typography>
      </FlexColBox>
    </Box>
  );
};

export default memo(ProductLineFilterChip);
